import { Box } from '@mentimeter/ragnar-ui/box';
import { animated, useTransition, type SpringValues } from '@react-spring/web';
import type { ReactNode } from 'react';

interface AnimatedOrderedListProps<T> {
  rows: T[];
  renderItem: (item: T, index: number) => ReactNode;
  itemHeight: number;
  disableAnimations: boolean;
  getOrderedListStyle?:
    | ((props: {
        index: number;
        key: string;
        length: number;
      }) => React.CSSProperties)
    | undefined;
}

export const AnimatedOrderedList = <T extends { key: string; y?: number }>({
  rows,
  renderItem,
  itemHeight,
  disableAnimations,
  getOrderedListStyle,
}: AnimatedOrderedListProps<T>) => {
  const transitions = useTransition(
    rows.map((data, i) => ({
      ...data,
      // allow y to be overridden by the data, otherwise use the default position
      y: data.y ?? itemHeight * i,
    })),

    {
      key: (item: any) => item.key,
      from: { opacity: 0 },
      leave: { opacity: 0 },
      enter: ({ y }) => ({
        transform: `translateY(${y}px)`,
        opacity: 1,
      }),
      update: ({ y }) => ({ transform: `translateY(${y}px)` }),
      immediate: disableAnimations,
    },
  );

  return (
    <Box width="100%" height="100%">
      {transitions((style, item, t, index) => {
        const orderedListStyle = getOrderedListStyle?.({
          index,
          key: item.key,
          length: rows.length,
        });
        return (
          <Box
            as={animated.div}
            position="absolute"
            width="100%"
            style={{
              zIndex: rows.length - index,
              top: 0,
              left: 0,
              willChange: 'transform, height, opacity',
              height: itemHeight,
              ...(style as SpringValues),
              ...orderedListStyle,
            }}
          >
            {renderItem(item, index)}
          </Box>
        );
      })}
    </Box>
  );
};
